import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { rpc, useAction } from "@vixio/data";
import { Button, Callout, Checkbox, Dialog, NumberInput, Select, Tabs, TextArea, TextInput } from "@vixio/ui";
import {
  CHOICE_TYPES, CONTACT_TYPES, FIELD_LABEL, SCORED_TYPES, optionLabel, optionValue,
  type FieldType, type FormDefinition, type FormItem, type FormRow, type IdentityMode,
} from "./api";

const ID_RE = /^[a-z][a-z0-9_]{0,39}$/;

function emptyDefinition(): FormDefinition {
  return { intro: "", items: [{ id: "q1", type: "short_text", text: "", required: true }] };
}

/** Problemas que a interface já consegue apontar; o banco (forms.validate_definition) confere tudo de novo. */
export function definitionProblems(def: FormDefinition, mode: IdentityMode): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  const dims = new Set((def.dimensions ?? []).map((d) => d.key));
  if (!def.items.length) out.push("Inclua ao menos um item.");
  def.items.forEach((it, i) => {
    const n = `Item ${i + 1}`;
    if (!ID_RE.test(it.id)) out.push(`${n}: identificador deve começar com letra e usar só a-z, 0-9 e _.`);
    if (ids.has(it.id)) out.push(`${n}: identificador "${it.id}" repetido.`);
    ids.add(it.id);
    if (it.text.trim().length < 2) out.push(`${n}: escreva o enunciado.`);
    if (CHOICE_TYPES.includes(it.type)) {
      const vals = (it.options ?? []).map(optionValue);
      if (vals.length < 2) out.push(`${n}: escolha precisa de pelo menos 2 opções.`);
      if (new Set(vals).size !== vals.length) out.push(`${n}: opções repetidas.`);
    }
    if (it.dimension && !dims.has(it.dimension)) out.push(`${n}: dimensão "${it.dimension}" não existe.`);
    if (mode === "anonymous" && (CONTACT_TYPES.includes(it.type) || it.maps_to)) {
      out.push(`${n}: formulário anônimo não aceita e-mail, telefone nem campo de identificação.`);
    }
  });
  return out;
}

function ItemEditor({
  item, index, total, dims, onChange, onMove, onRemove,
}: {
  item: FormItem;
  index: number;
  total: number;
  dims: Array<{ key: string; label?: string }>;
  onChange: (it: FormItem) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const [optText, setOptText] = useState((item.options ?? []).map(optionLabel).join("\n"));
  useEffect(() => setOptText((item.options ?? []).map(optionLabel).join("\n")), [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const isChoice = CHOICE_TYPES.includes(item.type);
  return (
    <fieldset className="vx-fieldset">
      <legend>
        Item {index + 1}
        <span className="muted"> · {FIELD_LABEL[item.type]}</span>
      </legend>
      <div className="vx-form-grid">
        <TextInput label="Identificador" value={item.id} onChange={(v) => onChange({ ...item, id: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} hint="Chave da resposta, ex.: q1" />
        <Select
          label="Tipo"
          value={item.type}
          onChange={(v) => onChange({ ...item, type: v as FieldType })}
          options={(Object.keys(FIELD_LABEL) as FieldType[]).map((t) => ({ value: t, label: FIELD_LABEL[t] }))}
        />
      </div>
      <TextArea label="Enunciado" value={item.text} onChange={(v) => onChange({ ...item, text: v })} rows={2} maxLength={500} />
      {isChoice ? (
        <TextArea
          label="Opções (uma por linha)"
          value={optText}
          rows={3}
          onChange={(v) => {
            setOptText(v);
            const labels = v.split("\n").map((s) => s.trim()).filter(Boolean);
            onChange({ ...item, options: labels });
          }}
        />
      ) : null}
      <div className="vx-form-grid">
        {SCORED_TYPES.includes(item.type) && dims.length > 0 ? (
          <Select
            label="Dimensão"
            value={item.dimension ?? ""}
            placeholder="Sem dimensão"
            onChange={(v) => onChange({ ...item, dimension: v || undefined })}
            options={dims.map((d) => ({ value: d.key, label: d.label || d.key }))}
          />
        ) : null}
        {item.type === "multi_choice" ? (
          <NumberInput label="Máximo de escolhas" value={item.max_choices ?? null} min={1} onChange={(v) => onChange({ ...item, max_choices: v ?? undefined })} />
        ) : null}
      </div>
      <div className="vx-row vx-row--between">
        <Checkbox label="Obrigatório" checked={item.required !== false} onChange={(c) => onChange({ ...item, required: c })} />
        <span className="vx-row">
          <Button size="sm" variant="ghost" iconOnly label="Subir" icon={<ArrowUp />} disabled={index === 0} onClick={() => onMove(-1)} />
          <Button size="sm" variant="ghost" iconOnly label="Descer" icon={<ArrowDown />} disabled={index === total - 1} onClick={() => onMove(1)} />
          <Button size="sm" variant="danger" iconOnly label="Remover item" icon={<Trash2 />} onClick={onRemove} />
        </span>
      </div>
    </fieldset>
  );
}

/**
 * Editor de versão: itens, dimensões e textos de abertura, com a mesma estrutura de `forms.validate_definition`.
 * A aba JSON dá acesso ao que o editor não cobre (consentimentos, pesos, inversão, `maps_to`).
 */
export function VersionEditorDialog({
  form, base, open, onOpenChange,
}: {
  form: FormRow;
  base: FormDefinition | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [def, setDef] = useState<FormDefinition>(emptyDefinition);
  const [scored, setScored] = useState(false);
  const [tab, setTab] = useState("itens");
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      const d = base ? (JSON.parse(JSON.stringify(base)) as FormDefinition) : emptyDefinition();
      setDef(d);
      setScored(Boolean(base && (base.items ?? []).some((i) => SCORED_TYPES.includes(i.type))));
      setTab("itens");
      setJson(JSON.stringify(d, null, 2));
      setJsonError(null);
      setTouched(false);
    }
  }, [open, base]);

  const create = useAction(() => rpc("forms", "create_version", { p_form: form.id, p_definition: def, p_scoring: scored ? {} : null }), {
    invalidate: [["forms"]],
    success: "Versão criada em rascunho",
    onSuccess: () => onOpenChange(false),
  });

  const dims = def.dimensions ?? [];
  const problems = definitionProblems(def, form.identity_mode);
  const hasLikert = def.items.some((i) => i.type === "likert");

  function setItem(i: number, it: FormItem) {
    setDef((d) => ({ ...d, items: d.items.map((x, j) => (j === i ? it : x)) }));
  }
  function move(i: number, dir: -1 | 1) {
    setDef((d) => {
      const items = [...d.items];
      const [x] = items.splice(i, 1);
      items.splice(i + dir, 0, x!);
      return { ...d, items };
    });
  }
  function nextId(): string {
    let n = def.items.length + 1;
    while (def.items.some((i) => i.id === `q${n}`)) n++;
    return `q${n}`;
  }
  function switchTab(v: string) {
    if (v === "json") {
      setJson(JSON.stringify(def, null, 2));
      setJsonError(null);
      setTab(v);
      return;
    }
    try {
      const parsed = JSON.parse(json) as FormDefinition;
      if (!parsed || !Array.isArray(parsed.items)) throw new Error("A definição precisa de uma lista \"items\".");
      setDef(parsed);
      setJsonError(null);
      setTab(v);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : "JSON inválido");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={base ? "Nova versão a partir da vigente" : "Primeira versão"}
      description={`${form.title} · ${form.identity_mode === "anonymous" ? "anônimo: sem campos de contato" : "a versão nasce em rascunho; publicar a torna imutável"}`}
      onSubmit={() => {
        setTouched(true);
        if (tab === "json") {
          switchTab("itens");
          return;
        }
        if (problems.length === 0) create.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Salvar rascunho
          </Button>
        </>
      }
    >
      <Tabs
        label="Editor da versão"
        value={tab}
        onValueChange={switchTab}
        tabs={[
          {
            value: "itens",
            label: "Itens",
            count: def.items.length,
            content: (
              <div className="vx-stack" style={{ paddingTop: 12 }}>
                <TextArea label="Texto de abertura" value={def.intro ?? ""} onChange={(v) => setDef({ ...def, intro: v })} rows={2} maxLength={2000} />
                <TextArea label="Aviso" hint="Opcional. Aparece junto do formulário e do resultado." value={def.disclaimer ?? ""} onChange={(v) => setDef({ ...def, disclaimer: v || undefined })} rows={2} maxLength={1000} />
                <Checkbox
                  label="Calcular pontuação no envio"
                  hint="Itens de escala, nota e NPS viram pontuação de 0 a 100 (geral e por dimensão)."
                  checked={scored}
                  onChange={setScored}
                />
                {hasLikert ? (
                  <div className="vx-form-grid">
                    <NumberInput label="Escala: mínimo" value={def.scale?.min ?? 1} onChange={(v) => setDef({ ...def, scale: { ...(def.scale ?? { max: 5 }), min: v ?? 1 } })} />
                    <NumberInput label="Escala: máximo" value={def.scale?.max ?? 5} onChange={(v) => setDef({ ...def, scale: { ...(def.scale ?? { min: 1 }), max: v ?? 5 } })} />
                    <TextInput
                      label="Rótulos da escala"
                      hint="Separados por vírgula, do menor para o maior."
                      value={(def.scale?.labels ?? []).join(", ")}
                      onChange={(v) =>
                        setDef({ ...def, scale: { min: def.scale?.min ?? 1, max: def.scale?.max ?? 5, labels: v.split(",").map((s) => s.trim()).filter(Boolean) } })
                      }
                    />
                  </div>
                ) : null}
                <fieldset className="vx-fieldset">
                  <legend>Dimensões</legend>
                  {dims.length === 0 ? <p className="muted">Sem dimensões: a pontuação geral é a média dos itens pontuáveis.</p> : null}
                  {dims.map((d, i) => (
                    <div key={i} className="vx-form-grid">
                      <TextInput
                        label={`Chave ${i + 1}`}
                        value={d.key}
                        onChange={(v) => setDef({ ...def, dimensions: dims.map((x, j) => (j === i ? { ...x, key: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") } : x)) })}
                      />
                      <div className="vx-row" style={{ alignItems: "flex-end", flexWrap: "nowrap" }}>
                        <TextInput label="Rótulo" className="vx-spacer" value={d.label ?? ""} onChange={(v) => setDef({ ...def, dimensions: dims.map((x, j) => (j === i ? { ...x, label: v } : x)) })} />
                        <Button size="sm" variant="danger" iconOnly label="Remover dimensão" icon={<Trash2 />} onClick={() => setDef({ ...def, dimensions: dims.filter((_, j) => j !== i) })} />
                      </div>
                    </div>
                  ))}
                  <div>
                    <Button size="sm" icon={<Plus />} onClick={() => setDef({ ...def, dimensions: [...dims, { key: `dim${dims.length + 1}`, label: "" }] })}>
                      Adicionar dimensão
                    </Button>
                  </div>
                </fieldset>
                {def.items.map((it, i) => (
                  <ItemEditor
                    key={`${i}-${it.id}`}
                    item={it}
                    index={i}
                    total={def.items.length}
                    dims={dims}
                    onChange={(x) => setItem(i, x)}
                    onMove={(dir) => move(i, dir)}
                    onRemove={() => setDef({ ...def, items: def.items.filter((_, j) => j !== i) })}
                  />
                ))}
                <div>
                  <Button icon={<Plus />} onClick={() => setDef({ ...def, items: [...def.items, { id: nextId(), type: "likert", text: "", required: true }] })}>
                    Adicionar item
                  </Button>
                </div>
                {touched && problems.length > 0 ? (
                  <Callout tone="warn" title="Revise antes de salvar">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {problems.slice(0, 8).map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </Callout>
                ) : null}
              </div>
            ),
          },
          {
            value: "json",
            label: "JSON",
            content: (
              <div className="vx-stack" style={{ paddingTop: 12 }}>
                <TextArea label="Definição completa" hint="Consentimentos, pesos, inversão e maps_to só por aqui." value={json} onChange={setJson} rows={18} error={jsonError} />
              </div>
            ),
          },
        ]}
      />
    </Dialog>
  );
}
